/**
 * CampaignDayDelayPure
 *
 * Connector band rendered between two day cards. Replaces the legacy
 * floating "[N] days" pill from `CGDelayStepPure.tsx`. Visually links the
 * preceding card to the next one with a horizontal dashed rule and a
 * centered, full-sentence wait label that includes both the duration
 * (pluralized) and the resolved absolute date the next day lands on.
 *
 * The component is display-only — it emits `onEdit` when the user clicks
 * the band. The host owns the actual edit form (e.g. the legacy
 * `DelayEdit` component) and toggles between display and edit modes.
 *
 * Render contract:
 *   - `days = 0`  → renders nothing (no wait between same-day groups)
 *   - `days >= 1` → renders the connector band
 */
import { cn } from '../cn';
import { Editor, General } from '../StrokeIcons';
import { weekDelayLabel } from './weekDelayLabel';

export type CampaignDayDelayPureProps = {
  /** Wait length in whole days. Anything <= 0 hides the component. */
  days: number;
  /** A wait under a day, in hours: the pill reads "wait 6 hours" rather than "wait 0 days". */
  hours?: number;
  /**
   * When true, the wait is calendar-week aligned — the pill reads "next week" /
   * "the week after next" / "in N weeks" instead of "wait N days".
   */
  alignWeek?: boolean;
  /** True when this delay diverges from the template (purple accent). */
  isDifferentFromTemplate?: boolean;
  /** Click handler for the wait pill. When omitted the pill is non-interactive. */
  onEdit?: () => void;
  /**
   * When set, surfaces an "+ add new day" pill alongside the wait pill on
   * the same connector row. Lets the user insert an extra day between two
   * existing day cards without breaking the visual flow with a separate
   * full-width trigger button.
   */
  onAddNewDay?: () => void;
  className?: string;
};

export function CampaignDayDelayPure({
  days,
  hours,
  alignWeek,
  isDifferentFromTemplate,
  onEdit,
  onAddNewDay,
  className,
}: CampaignDayDelayPureProps) {
  const interactive = onEdit != null;
  // The wait pill stays visible whenever it's editable (even at 0 days)
  // so the user can click to bump the delay above zero. Without this the
  // pill would disappear at 0 and there'd be no way to grow it back.
  const showWaitPill = days > 0 || hours != null || interactive;
  if (!showWaitPill && onAddNewDay == null) return null;

  const dayLabel = days === 1 ? 'day' : 'days';

  return (
    <div
      data-testid="campaign-day-delay"
      data-days={days}
      data-different-from-template={isDifferentFromTemplate ? 'true' : 'false'}
      // No `w-full` here — block divs already fill their parent's
      // content box minus margins. Combining `w-full` with the host's
      // `mx-2` would set width to 100% AND add 16px margin, overflowing
      // by 16px and shifting the band's center 8px to the right of the
      // surrounding cards. (Caught by a vertical-spine alignment bug.)
      className={cn('relative flex flex-col', className)}>
      {/*
        Centered vertical spine that visually links the card above the
        delay band to the card below it. The pills render on top with a
        solid background so the spine appears to thread through them.
      */}
      <span
        aria-hidden
        data-testid="campaign-day-delay-spine"
        className={cn(
          'pointer-events-none absolute left-1/2 top-0 h-full w-px -translate-x-1/2',
          isDifferentFromTemplate
            ? 'bg-purple-300 dark:bg-purple-700'
            : 'bg-lynch-200 dark:bg-slate-700',
        )}
      />
      {onAddNewDay != null && (
        <div
          data-testid="campaign-day-delay-add-row"
          className="relative z-10 flex w-full items-center justify-center px-2 pb-2 pt-3">
          <button
            type="button"
            data-testid="campaign-day-delay-add"
            onClick={onAddNewDay}
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full border bg-white px-3 py-1 text-xs font-medium shadow-xs transition-colors dark:bg-slate-900',
              isDifferentFromTemplate
                ? 'border-purple-300 text-lynch-700 hover:border-purple-400 hover:bg-purple-50 hover:text-purple-700 dark:border-purple-700 dark:text-lynch-200 dark:hover:border-purple-500 dark:hover:bg-purple-950 dark:hover:text-purple-300'
                : 'border-lynch-300 text-lynch-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 dark:border-slate-600 dark:text-lynch-200 dark:hover:border-blue-500 dark:hover:bg-blue-950 dark:hover:text-blue-300',
            )}>
            <General.Plus className="size-3" />
            add new day
          </button>
        </div>
      )}
      <div
        data-testid="campaign-day-delay-wait-row"
        className="relative z-10 flex w-full items-center justify-center px-2 pb-2 pt-3">
        {showWaitPill && (
          <button
            type="button"
            data-testid="campaign-day-delay-pill"
            disabled={!interactive}
            onClick={onEdit}
            className={cn(
              // No `gap-*` here — the pencil's own slot owns its left
              // margin so we can collapse it to 0 when hidden, keeping
              // the text perfectly centered in the rest state. On hover
              // the slot expands to show the pencil and the text shifts
              // left as a natural consequence.
              'group inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs transition-colors',
              isDifferentFromTemplate
                ? 'border-purple-300 bg-purple-50 text-purple-800 dark:border-purple-700 dark:bg-purple-950/40 dark:text-purple-200'
                : 'border-lynch-200 bg-white text-lynch-700 dark:border-slate-700 dark:bg-slate-900 dark:text-lynch-200',
              interactive && [
                'cursor-pointer',
                isDifferentFromTemplate
                  ? 'hover:bg-purple-100 dark:hover:bg-purple-950/60'
                  : 'hover:border-lynch-300 hover:bg-lynch-50 dark:hover:border-slate-600 dark:hover:bg-slate-800',
              ],
              !interactive && 'cursor-default',
            )}>
            <span className="font-medium">
              {alignWeek && (
                <span data-testid="campaign-day-delay-days" className="font-semibold">
                  {weekDelayLabel(Math.max(1, Math.round(days / 7)))}
                </span>
              )}
              {!alignWeek && (
                <>
                  wait{' '}
                  <span data-testid="campaign-day-delay-days" className="font-semibold">
                    {hours != null ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : `${days} ${dayLabel}`}
                  </span>
                </>
              )}
            </span>
            {interactive && (
              <span
                aria-hidden
                className="ml-0 inline-flex w-0 items-center overflow-hidden opacity-0 transition-all duration-150 group-hover:ml-1.5 group-hover:w-3 group-hover:opacity-70">
                <Editor.Pencil03
                  data-testid="campaign-day-delay-edit-icon"
                  className={cn(
                    'size-3 shrink-0',
                    isDifferentFromTemplate ? 'text-purple-700 dark:text-purple-300' : 'text-lynch-500',
                  )}
                />
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
